import Crypto from "crypto";
import {SnapshotEntity} from "./types";

// Dairesel referansları yakalamak için: bir nesne/dizi kendi soyunda (ata zincirinde)
// tekrar görülürse "[Circular]" yazılır. Kardeş dallarda aynı referansın tekrar
// geçmesi (paylaşılan referans, gerçek döngü değil) yanlış pozitif ÜRETMEZ — çünkü
// bir dal işlenip bittiğinde WeakSet'ten çıkarılır (yalnız o anki soy zinciri tutulur).
function donustur(deger: unknown, gorulenler: WeakSet<object>): string {
    if (deger === undefined || deger === null) {
        return "null";
    }

    const tip = typeof deger;

    if (tip === "number") {
        const sayi = deger as number;
        // Tırnaksız marker kasıtlı: JSON.stringify() her zaman string'i tırnak içine
        // alır, bu yüzden bu bare token'lar literal "NaN"/"Infinity"/"-Infinity" string
        // alanlarıyla ASLA çakışmaz (K1 açığı 2 — tip bilgisi kanonik temsilde korunur).
        if (Number.isNaN(sayi)) return "NaN";
        if (sayi === Infinity) return "Infinity";
        if (sayi === -Infinity) return "-Infinity";
        return JSON.stringify(sayi);
    }

    if (tip === "string" || tip === "boolean") {
        return JSON.stringify(deger);
    }

    if (tip === "bigint") {
        return JSON.stringify((deger as bigint).toString());
    }

    if (deger instanceof Date) {
        // isNaN tarih ("Invalid Date") de deterministik bir temsil üretmeli; tırnaksız
        // marker (yukarıdaki NaN/Infinity ile aynı gerekçe) literal "Invalid Date"
        // string alanıyla çakışmaz.
        return Number.isNaN(deger.getTime()) ? "Invalid Date" : JSON.stringify(deger.toISOString());
    }

    if (Array.isArray(deger)) {
        if (gorulenler.has(deger)) return '"[Circular]"';
        gorulenler.add(deger);
        // dizi sırası korunur — dizi elemanları sıralı veri taşır
        const parcalar = deger.map((eleman) => donustur(eleman, gorulenler));
        gorulenler.delete(deger);
        return `[${parcalar.join(",")}]`;
    }

    if (tip === "object") {
        const nesne = deger as Record<string, unknown>;
        if (gorulenler.has(nesne)) return '"[Circular]"';
        gorulenler.add(nesne);
        const anahtarlar = Object.keys(nesne).sort();
        const parcalar: string[] = [];
        for (const anahtar of anahtarlar) {
            const alanDegeri = nesne[anahtar];
            if (alanDegeri === undefined) continue; // undefined degerler atlanir
            parcalar.push(`${JSON.stringify(anahtar)}:${donustur(alanDegeri, gorulenler)}`);
        }
        gorulenler.delete(nesne);
        return `{${parcalar.join(",")}}`;
    }

    // function, symbol ve tanınmayan diğer tipler için son çare: deterministik string
    return JSON.stringify(String(deger));
}

// Verilen değeri kanonik (anahtarları alfabetik sıralı, dizi sırası korunmuş,
// undefined atlanmış, null/NaN/Infinity/Date deterministik) bir JSON string'ine çevirir.
// Aynı içerik → her koşumda birebir aynı string; hash bu string üzerinden alınır.
export function kanonikJson(deger: unknown): string {
    return donustur(deger, new WeakSet<object>());
}

// entities dizisini key'e göre sıralar (eşitlikte label'a, o da eşitse entity'nin tüm
// kanonik JSON'una göre); saf < > karşılaştırması kullanılır (localeCompare DEĞİL) —
// sonuç yerelden (locale) bağımsız olmalı.
function siraliEntities(entities: SnapshotEntity[]): SnapshotEntity[] {
    // Üçüncü tie-break (key VE label eşit, attrs farklı — ör. takbis'te tekrar eden
    // tasinmazId) olmadan JS'in STABIL sort'u devreye girip nihai sırayı girdi
    // dizisinin sırasına bağlı bırakır: aynı mantıksal veri kümesi, UYAP'ın döndürdüğü
    // farklı dizi sırasıyla farklı hash üretebilirdi (K1 açığı 1). Her entity'nin tüm
    // kanonik JSON'u (girdiden bağımsız, içerik-tabanlı) tek seferde önceden hesaplanıp
    // decorate-sort-undecorate ile kıyaslanır — girdi dizisi mutasyona uğratılmaz.
    const decore = entities.map((entity) => ({entity, json: kanonikJson(entity)}));
    decore.sort((a, b) => {
        if (a.entity.key < b.entity.key) return -1;
        if (a.entity.key > b.entity.key) return 1;
        if (a.entity.label < b.entity.label) return -1;
        if (a.entity.label > b.entity.label) return 1;
        if (a.json < b.json) return -1;
        if (a.json > b.json) return 1;
        return 0;
    });
    return decore.map((d) => d.entity);
}

// SnapshotPaketi.hash — entities key'e (eşitlikte label'a) göre sıralanır, kanonik
// JSON'a çevrilir ve sha1'i alınır. Sunucu "değişti mi" kararını yalnız bu hash'ten verir.
export function kanonikHash(entities: SnapshotEntity[]): string {
    const sirali = siraliEntities(entities);
    return Crypto.createHash("sha1")
        .update(kanonikJson(sirali))
        .digest()
        .toString("hex");
}
